import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { OrderData, PassStatus } from '../api';
import { PunchProgress } from './components/PunchProgress';
import { BookingCard, UpcomingEntryType } from './components/BookingCard';
import { RedeemableExperiencesList } from './components/RedeemableExperiencesList';
import { EmptyState } from './components/EmptyState';
import { CancellationSheet } from './components/CancellationSheet';
import { BuyAgainSheet } from './components/BuyAgainSheet';
import { QrCheckinSheet } from './components/QrCheckinSheet';
import {
  canBuyPassAgain,
  getEffectiveNumPunches,
  getExperienceName,
  getCurrentScheduleSelections,
  getExpirationDate,
  getPassOrderPage,
  getPassOrderPageName,
  getUsedPunches,
  hasPassStarted,
  isFixedScheduleMonthly,
  isMembership,
  isPassExpired,
} from './utils/passUtils';
import { getPassCancelHoursBefore, isPassCancelAllowed } from './utils/passCancellationUtils';
import { useManagePasses } from './ManagePassesContext';
import { useLanguage } from '../utils/GeneralUtils';
import { getDateFormat, getTimeFormat } from '../utils/getDateAndTimeFormat';
import { formatSlotLabel, groupSelectionsByOrderPage } from '../shared/passScheduleSelector/utils';
import { MembershipSummary } from './components/MembershipSummary';
import { getStatusChip } from './components/passStatusChip';

export const PassDetailView = () => {
  const { t, i18n } = useTranslation();
  const language = useLanguage();
  const navigate = useNavigate();
  const { passId } = useParams<{ passId: string }>();
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const isRtl = i18n.dir() === 'rtl';
  const { passes, orders, account, cancelOrder } = useManagePasses();
  const { longDateFormat } = getDateFormat(account);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<OrderData | null>(null);
  const [qrOrder, setQrOrder] = useState<OrderData | null>(null);
  const [buyAgainOpen, setBuyAgainOpen] = useState(false);

  const pass = passes.find((p) => p.id === passId);
  if (!pass || !account) return null;

  const passOrderPage = getPassOrderPage(account, pass.order_page_id);
  const numPunches = getEffectiveNumPunches(pass, passOrderPage);
  const used = getUsedPunches(pass);
  const remaining = numPunches !== null ? numPunches - used : null;
  const passName = getPassOrderPageName(passOrderPage, language, t);
  const expired = isPassExpired(pass, passOrderPage);
  const expirationDate = getExpirationDate(pass, passOrderPage);
  const isNotYetActive = pass.status === PassStatus.ACTIVE && !hasPassStarted(pass);
  const canBuyAgain = canBuyPassAgain(pass, passOrderPage, account, passes);
  const membership = isMembership(passOrderPage);
  // Punch counts mean nothing for a membership: an unlimited one gets the infinity chip instead, and
  // a fixed-schedule one is described by the weekly schedule section further down.
  const showUnlimitedChip = membership && !isFixedScheduleMonthly(passOrderPage?.pass_type);
  const showSummaryCard = !membership || showUnlimitedChip || pass.guest_pass_remaining != null || canBuyAgain;

  const passOrders = orders.filter((o) => pass.redeemed_order_ids?.includes(o.id));
  const upcomingOrders = passOrders
    .filter((o) => o.date && moment(o.date).isAfter(moment()) && o.status !== 'archived')
    .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
  const pastOrders = passOrders
    .filter((o) => o.date && moment(o.date).isBefore(moment()))
    .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());

  const timeFormat = getTimeFormat(account);
  const scheduleGroups = groupSelectionsByOrderPage(getCurrentScheduleSelections(pass), account, language);
  const isMultiSchedulePage = scheduleGroups.length > 1;

  const handleCancelClick = (order: OrderData) => {
    setOrderToCancel(order);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async (confirmed: boolean) => {
    if (confirmed && orderToCancel) {
      await cancelOrder(orderToCancel.id);
    }
    setCancelDialogOpen(false);
    setOrderToCancel(null);
  };

  const BackArrow = isRtl ? ArrowForwardIcon : ArrowBackIcon;

  return (
    <Grid container flexDirection="column" gap={isSmallScreen ? 2 : 3} pb={10}>
      <Grid container alignItems="center">
        <IconButton onClick={() => navigate(-1)} sx={{ minHeight: 44, minWidth: 44 }}>
          <BackArrow />
        </IconButton>
      </Grid>

      <Grid
        container
        flexDirection="column"
        gap={1}
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 2,
          p: isSmallScreen ? 2 : 2.5,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
        <Grid container justifyContent="space-between" alignItems="center" gap={1}>
          <Grid item xs>
            <Typography variant="h5" fontWeight={600}>
              {passName}
            </Typography>
          </Grid>
          {getStatusChip(pass, numPunches, passOrderPage, t)}
        </Grid>
        <Typography variant="body2" color="text.secondary">
          {t('manage_passes.pass.code', { code: pass.code })}
        </Typography>
      </Grid>

      {showSummaryCard && (
        <Grid
          container
          flexDirection="column"
          gap={1}
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            p: isSmallScreen ? 2 : 2.5,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}>
          {showUnlimitedChip && (
            <Grid container>
              <Chip
                label={`∞ ${t('manage_passes.pass.status.unlimited')}`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Grid>
          )}
          {!membership && (
            <>
              <PunchProgress used={used} total={numPunches} large />
              {numPunches !== null && (
                <Typography variant="body2">
                  {t('manage_passes.detail.punchSummary', { used, total: numPunches, remaining })}
                </Typography>
              )}
            </>
          )}
          {pass.guest_pass_remaining != null && (
            <Typography variant="body2">
              {t('manage_passes.detail.guestSummary', { remaining: pass.guest_pass_remaining })}
            </Typography>
          )}
          {canBuyAgain && (
            <Grid container justifyContent="flex-end" mt={1}>
              <Button variant="outlined" size="small" onClick={() => setBuyAgainOpen(true)} sx={{ minHeight: 36 }}>
                {t('manage_passes.buyAgain.cta')}
              </Button>
            </Grid>
          )}
        </Grid>
      )}

      {membership ? (
        <MembershipSummary pass={pass} passOrderPage={passOrderPage} account={account} variant="detail" />
      ) : (
        <Grid container flexDirection="column" gap={0.5}>
          {isNotYetActive && pass.start_date && (
            <Typography variant="body1" color="warning.main">
              {t('manage_passes.pass.startsOn', { date: moment(pass.start_date).format(longDateFormat) })}
            </Typography>
          )}
          {expirationDate ? (
            <Typography variant="body1" color={expired ? 'error' : 'text.primary'}>
              {expired
                ? t('manage_passes.pass.expiredOn', { date: moment(expirationDate).format(longDateFormat) })
                : t('manage_passes.pass.validUntil', { date: moment(expirationDate).format(longDateFormat) })}
            </Typography>
          ) : (
            <Typography variant="body1" color="text.secondary">
              {t('manage_passes.pass.noExpiry')}
            </Typography>
          )}
        </Grid>
      )}

      <RedeemableExperiencesList redeemableOn={passOrderPage?.redeemable_on} account={account} />

      {scheduleGroups.length > 0 && (
        <Grid container flexDirection="column" gap={isSmallScreen ? 1 : 1.5}>
          <Typography variant="h6">{t('manage_passes.detail.yourSchedule')}</Typography>
          {scheduleGroups.map(({ opId, pageLabel, sorted }) => (
            <Grid key={opId} container flexDirection="column" gap={0.75}>
              {isMultiSchedulePage && pageLabel && (
                <Typography variant="subtitle2" fontWeight={600}>
                  {pageLabel}
                </Typography>
              )}
              <Grid container gap={0.75}>
                {sorted.map((sel) => (
                  <Chip
                    key={`${sel.order_page_id}-${sel.weekday}-${sel.start_time}`}
                    size="small"
                    label={formatSlotLabel(t, sel.weekday, sel.start_time, timeFormat)}
                  />
                ))}
              </Grid>
            </Grid>
          ))}
        </Grid>
      )}

      <Grid container flexDirection="column" gap={isSmallScreen ? 1.5 : 2}>
        <Typography variant="h6">{t('manage_passes.detail.redemptionHistory')}</Typography>
        {upcomingOrders.length > 0 &&
          upcomingOrders.map((order) => (
            <BookingCard
              key={order.id}
              entry={{ type: UpcomingEntryType.BOOKING, data: order }}
              account={account}
              onClick={account.qr_checkin_enabled ? () => setQrOrder(order) : undefined}
              onCancel={() => handleCancelClick(order)}
            />
          ))}
        {pastOrders.length > 0 &&
          pastOrders.map((order) => (
            <BookingCard
              key={order.id}
              entry={{ type: UpcomingEntryType.BOOKING, data: order }}
              account={account}
              muted
            />
          ))}
        {passOrders.length === 0 && <EmptyState message={t('manage_passes.detail.noRedemptions')} />}
      </Grid>

      <CancellationSheet
        isOpen={cancelDialogOpen}
        onClose={handleCancelConfirm}
        experienceName={orderToCancel ? getExperienceName(account, orderToCancel.order_page_id, language) : ''}
        bookingDate={orderToCancel?.date ? moment(orderToCancel.date).format(longDateFormat) : ''}
        cancelHoursBefore={orderToCancel ? getPassCancelHoursBefore(account, orderToCancel.order_page_id) : undefined}
        cancelAllowed={
          !orderToCancel ||
          isPassCancelAllowed(
            getPassCancelHoursBefore(account, orderToCancel.order_page_id),
            orderToCancel.date || '',
            orderToCancel.time,
          )
        }
        guestName={
          orderToCancel?.guest_order_id
            ? orders.find((o) => o.id === orderToCancel.guest_order_id)?.firstname
            : undefined
        }
      />

      {canBuyAgain && passOrderPage && (
        <BuyAgainSheet
          isOpen={buyAgainOpen}
          onClose={() => setBuyAgainOpen(false)}
          pass={pass}
          passOrderPage={passOrderPage}
        />
      )}

      <QrCheckinSheet
        isOpen={!!qrOrder}
        onClose={() => setQrOrder(null)}
        qrUrl={qrOrder?.qr_url}
        experienceName={qrOrder ? getExperienceName(account, qrOrder.order_page_id, language) : ''}
        bookingDate={qrOrder?.date ? moment(qrOrder.date).format(longDateFormat) : ''}
      />
    </Grid>
  );
};
